import { mapDefaults } from "@/info/map-defaults";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { Bounds } from "@/providers/search/stores/map-store";
import type { Filters, Location, LocationType } from "@/types/filters";
import { calculateBounds } from "@/utils/map/calculate-bounds";
import { logger } from "../logger";

export interface TransformFiltersParams {
	filters: Partial<Filters>;
	haveLocationsChanged?: boolean;
	mapBounds?: Bounds;
	mapZoom?: number;
	selectedLocationId?: string;
	from?: "filters" | "nav-overlay" | "search-area" | "postSSR" | "ssr";
}

export interface GraphQLFilters extends Record<string, unknown> {
	bounds?: Bounds[];
	locations?: Location[];
}

interface LocationWithCoordinates {
	id: string;
	name: string;
	type: LocationType;
	coordinates?: {
		latitude: number;
		longitude: number;
	};
}

function getLocationCoordinates(location: LocationWithCoordinates): { lat: number; lng: number } | undefined {
	if (
		location.coordinates?.latitude !== undefined &&
		location.coordinates?.longitude !== undefined &&
		!isNaN(location.coordinates.latitude) &&
		!isNaN(location.coordinates.longitude)
	) {
		return {
			lat: location.coordinates.latitude,
			lng: location.coordinates.longitude,
		};
	}
	return undefined;
}

// Optimized transform function with better performance and memory usage
export function transformFilters({
	filters,
	haveLocationsChanged,
	mapBounds,
	mapZoom,
	selectedLocationId,
	from,
}: TransformFiltersParams): { filtersForGraphQL: GraphQLFilters; newFilters: Partial<Filters> } {
	const newFilters = { ...filters };

	const transformed: GraphQLFilters = Object.create(null); // Use null prototype for better performance

	let selectedLocation;
	if (filters.locations?.length) {
		if (selectedLocationId) {
			selectedLocation = filters.locations.find(location => location.id === selectedLocationId);
		}
	}

	const zoom = selectedLocation?.type === "project" ? mapDefaults.projectZoom : mapDefaults.zoom;

	// Handle locations and bounds based on the search source
	switch (from) {
		case "filters":
			// From Filters modal - clicking on 'Apply Filters' button
			if (filters.locations?.length) {
				if (haveLocationsChanged) {
					// If locations changed, send the last location (as if SSR with last location) - without bounds, zoom reset
					const lastLocation = filters.locations[filters.locations.length - 1];
					const coordinates = getLocationCoordinates(lastLocation);
					newFilters.selectedLocationId = lastLocation.id;
					newFilters.coordinates = coordinates;
					newFilters.zoom = lastLocation.type === "project" ? mapDefaults.projectZoom : mapDefaults.zoom;

					if (coordinates) {
						transformed.bounds = [
							calculateBounds({
								center: coordinates,
								currentBounds: mapBounds,
								currentZoom: mapZoom,
								newZoom: newFilters.zoom,
							}),
						];
						newFilters.bounds = transformed.bounds;
						newFilters.coordinates = coordinates;
					} else {
						// should not happen ideally
						logger.error("transformFilters: nav-overlay case with no coordinates");
					}
					// Don't include bounds to reset zoom
				} else {
					// If no location change, use existing bounds in filters
					if (mapBounds) {
						transformed.bounds = [mapBounds];

						newFilters.bounds = transformed.bounds;
						newFilters.zoom = mapDefaults.zoom;
						newFilters.coordinates = {
							lat: parseFloat(((mapBounds.north + mapBounds.south) / 2).toFixed(6)),
							lng: parseFloat(((mapBounds.east + mapBounds.west) / 2).toFixed(6)),
						};
					} else {
						// this case should not exist
						logger.error("transformFilters: filters case with no bounds or location");
					}
				}
			} else {
				// get map bounds if custom selected area - else default bounds if no locations exist
				if (filters.selectedLocationId === "custom" && mapBounds) {
					transformed.bounds = [mapBounds];
					newFilters.coordinates = {
						lat: parseFloat(((mapBounds.north + mapBounds.south) / 2).toFixed(6)),
						lng: parseFloat(((mapBounds.east + mapBounds.west) / 2).toFixed(6)),
					};
				} else {
					transformed.bounds = [
						calculateBounds({
							center: mapDefaults.center,
						}),
					];
					newFilters.coordinates = mapDefaults.center;
				}
				newFilters.bounds = transformed.bounds;
				newFilters.zoom = mapDefaults.zoom;
			}
			break;

		case "nav-overlay":
			// From Map view - clicking on Nav Overlay Location Chips
			// The clicked location is sent without bounds and zoom is reset
			if (selectedLocation) {
				const coordinates = getLocationCoordinates(selectedLocation);
				if (coordinates) {
					transformed.bounds = [
						calculateBounds({
							center: coordinates,
							currentBounds: mapBounds,
							currentZoom: mapZoom,
							newZoom: zoom,
						}),
					];

					newFilters.bounds = transformed.bounds;
					newFilters.coordinates = coordinates;
				} else {
					// should not happen ideally
					logger.error("transformFilters: nav-overlay case with no coordinates");
				}

				newFilters.selectedLocationId = selectedLocation.id;
				newFilters.zoom = zoom;
			} else {
				// this case should not exist
				logger.error("transformFilters: nav-overlay case with no bounds or location");
			}

			break;

		case "search-area":
			// From Map view - click on 'Search this area'
			// The current bounds are sent
			if (mapBounds) {
				transformed.bounds = [mapBounds];

				newFilters.bounds = transformed.bounds;
				newFilters.selectedLocationId = "custom";
				newFilters.coordinates = {
					lat: parseFloat(((mapBounds.north + mapBounds.south) / 2).toFixed(6)),
					lng: parseFloat(((mapBounds.east + mapBounds.west) / 2).toFixed(6)),
				};
				newFilters.zoom = mapZoom;
				// Don't include locations when using bounds
			} else {
				// this case should not exist
				logger.error("transformFilters: search-area case with no bounds or location");
			}
			break;

		case "postSSR":
			if (!mapBounds) {
				// this case should not exist
				logger.error("transformFilters: postSSR case with no bounds or location");
				break;
			}
			// SSR - based on url
			// Selected location are sent with bounds
			// transformed.locations = [selectedLocation];
			transformed.bounds = [mapBounds];

			newFilters.bounds = transformed.bounds;
			newFilters.coordinates = {
				lat: parseFloat(((mapBounds.north + mapBounds.south) / 2).toFixed(6)),
				lng: parseFloat(((mapBounds.east + mapBounds.west) / 2).toFixed(6)),
			};

			if (selectedLocation) {
				newFilters.selectedLocationId = selectedLocationId;
				newFilters.zoom = selectedLocation.type === "project" ? mapDefaults.projectZoom : mapDefaults.zoom;
			}
			break;

		case "ssr":
			// SSR - based on url
			// All locations are sent so they can be enriched. Filter will happen on selectedLocationId or first location
			if (filters.locations?.length) {
				transformed.locations = filters.locations;
				// Don't include bounds for SSR if location is given
			} else {
				// if bounds or center is in url, use that
				transformed.bounds =
					filters.bounds || (filters.coordinates ? [calculateBounds({ center: filters.coordinates })] : undefined);
				transformed.zoom = filters.zoom;
			}
			break;

		default:
			logger.error("transformFilters: unknown search source", from);
			break;
	}

	// Always include the selected location in GraphQL filters
	if (selectedLocation) transformed.locations = [selectedLocation];
	// Optimized propertyType transformation with reduced object lookups
	if (filters.propertyType) {
		const propertyTypeOption = propertyTypeOptions.find(type => type?.value === filters.propertyType);
		const subTypeOptions = propertyTypeOption?.options;

		if (subTypeOptions?.length) {
			let subTypeOption = subTypeOptions[0]; // Default to first option

			if (filters.subType) {
				const foundSubType = subTypeOptions.find(type => type?.value === filters.subType);
				if (foundSubType) {
					subTypeOption = foundSubType;
				}
			}

			transformed.propertyType = {
				value: subTypeOption.value,
				label: subTypeOption.label,
			};
		}
	}

	// Optimized range values transformation
	if (filters.bedrooms?.min != null && filters.bedrooms?.max != null) {
		transformed.bedrooms = [String(filters.bedrooms.min), String(filters.bedrooms.max)];
	}

	// Optimized numeric filters with single null check pattern
	if ((filters.bathroomsMin ?? 0) > 0) {
		transformed.bathrooms = filters.bathroomsMin;
	}

	if (filters.budget?.min != null && filters.budget?.max != null) {
		transformed.budget = {
			min: Math.round(filters.budget.min),
			max: Math.round(filters.budget.max),
		};
	}

	if ((filters.builtupAreaMin ?? 0) > 0) {
		transformed.builtUpArea = filters.builtupAreaMin;
	}

	if (filters.floors?.min != null && filters.floors?.max != null) {
		transformed.floors = {
			value: String(filters.floors.min),
		};
	}

	if ((filters.balconiesMin ?? 0) > 0) {
		transformed.balconies = filters.balconiesMin;
	}

	if ((filters.parkingsMin ?? 0) > 0) {
		transformed.parkings = filters.parkingsMin;
	}

	if (filters.furnishing) {
		transformed.furnishingType = filters.furnishing;
	}

	if (filters.rentalValue && (filters.rentalValue.min ?? 0) > 0 && (filters.rentalValue.max ?? 0) > 0) {
		transformed.rentalValue = `min:${filters.rentalValue.min},max:${filters.rentalValue.max}`;
	}

	// Optimized array fields processing
	if (filters.levelType?.length) {
		transformed.level = filters.levelType;
	}

	if (filters.amenities?.length) {
		transformed.amenities = filters.amenities.map(amenity => amenity.code);
	}

	if (filters.status?.length || (typeof filters.status === "string" && filters.status)) {
		transformed.constructionStatus = filters.status;
	}
	// needs locations in case of projects but not for regular units
	if (filters.locations?.length) {
		const type = filters.locations?.find(el => el.id === filters.selectedLocationId)?.type;

		if (type === "project") {
			// Only override if not already set by specific case logic
			if (!transformed.locations) {
				transformed.locations = filters.locations.map(({ name, type }) => ({ id: "", name, type }));
			}
		}
	}

	// Optimized additional keys processing with direct assignments
	const additionalKeys = [
		"city",
		"anchors",
		"documents",
		"waterSources",
		"spaces",
		"possession",
		"maxAge",
		"facing",
		"saleStatus",
		"constructionStatus",
		"spuType",
		"hideType",
		"radius",
	];

	for (const key of additionalKeys) {
		const value = filters[key as keyof Filters];
		if (value != null && (Array.isArray(value) ? value.length > 0 : true)) {
			transformed[key] = value;
		}
	}

	return { filtersForGraphQL: transformed, newFilters };
}
